import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AppShell from "@/pages/AppShell";
import StudentDashboard from "@/pages/StudentDashboard";
import ParentDashboard from "@/pages/ParentDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import AgendaPage from "@/pages/AgendaPage";
import PerformancePage from "@/pages/PerformancePage";
import TutorPage from "@/pages/TutorPage";
import StudyPlanPage from "@/pages/StudyPlanPage";
import PaymentsPage from "@/pages/PaymentsPage";
import WebhooksPage from "@/pages/WebhooksPage";
import MaterialsPage from "@/pages/MaterialsPage";
import LessonsPage from "@/pages/LessonsPage";
import AnamnesisPage from "@/pages/AnamnesisPage";
import TutoringPage from "@/pages/TutoringPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import DataRightsPage from "@/pages/DataRightsPage";
import TeachersPage from "@/pages/TeachersPage";
import TermsPage from "@/pages/TermsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";

import CookieConsent from "@/components/CookieConsent";
import "@/App.css";

function RoleHome() {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 font-heading text-2xl">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "teacher") return <Navigate to="/teacher" replace />;
  if (user.role === "parent") return <Navigate to="/parent" replace />;
  // Aluno com acesso não liberado vai direto para a anamnese
  if (user.role === "student" && user.access_unlocked === false) return <Navigate to="/anamnesis" replace />;
  return <Navigate to="/student" replace />;
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <div className="p-10 font-heading text-2xl">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // Aluno com acesso ainda não liberado só pode navegar em /anamnesis (e /data-rights por segurança/LGPD)
  if (user.role === "student" && user.access_unlocked === false) {
    const allowed = ["/anamnesis", "/change-password", "/data-rights"];
    if (!allowed.some(p => location.pathname.startsWith(p))) return <Navigate to="/anamnesis" replace />;
  }
  return <AppShell>{children}</AppShell>;
}

function ForcedChangePassword() {
  const { user } = useAuth();
  if (user === null) return <div className="p-10 font-heading text-2xl">Carregando…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to="/" replace />;
  return <ChangePasswordPage />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <CookieConsent />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/change-password" element={<ForcedChangePassword />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/data-rights" element={<Protected><DataRightsPage /></Protected>} />
            <Route path="/" element={<RoleHome />} />
            <Route path="/student" element={<Protected roles={["student"]}><StudentDashboard /></Protected>} />
            <Route path="/parent" element={<Protected roles={["parent"]}><ParentDashboard /></Protected>} />
            <Route path="/admin" element={<Protected roles={["admin"]}><AdminDashboard /></Protected>} />
            <Route path="/teacher" element={<Protected roles={["teacher"]}><TeacherDashboard /></Protected>} />
            <Route path="/teachers" element={<Protected roles={["admin"]}><TeachersPage /></Protected>} />
            <Route path="/students-manage" element={<Protected roles={["admin", "teacher"]}><AdminDashboard /></Protected>} />
            <Route path="/agenda" element={<Protected><AgendaPage /></Protected>} />
            <Route path="/performance" element={<Protected><PerformancePage /></Protected>} />
            <Route path="/tutor" element={<Protected roles={["student", "admin", "teacher"]}><TutorPage /></Protected>} />
            <Route path="/plan" element={<Protected roles={["student", "admin", "teacher"]}><StudyPlanPage /></Protected>} />
            <Route path="/payments" element={<Protected><PaymentsPage /></Protected>} />
            <Route path="/materials" element={<Protected><MaterialsPage /></Protected>} />
            <Route path="/lessons" element={<Protected roles={["admin", "teacher", "student"]}><LessonsPage /></Protected>} />
            <Route path="/anamnesis" element={<Protected roles={["admin", "teacher", "student"]}><AnamnesisPage /></Protected>} />
            <Route path="/tutoring" element={<Protected><TutoringPage /></Protected>} />
            <Route path="/webhooks" element={<Protected roles={["admin"]}><WebhooksPage /></Protected>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
